// M014 Phase 14.1: shared provider-keys panel.
// Rendered by both the standalone /settings page (SettingsPage) and
// the in-project Settings tab (SettingsTab), so the surface stays in
// sync across both routes. Coral's visual-parity gate: this renders
// identically inside either chrome.

import { useCallback, useEffect, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import {
  ApiError,
  listProviderKeys,
  revokeProviderKey,
  setProviderKey,
  type ProviderKeySummary,
} from "../api/client";

// Sandbox runtime is Waytide-managed (staff key on the API) — there's
// no BYOA path, so the runtime provider isn't on this panel.
const SUPPORTED_PROVIDERS = [
  {
    id: "openrouter",
    label: "OpenRouter",
    hint: "One key for broad model coverage. Sits behind Anthropic, OpenAI, Google, Meta, and more.",
  },
  {
    id: "r2",
    label: "Cloudflare R2",
    hint: "Artifact storage for sandbox outputs (screenshots, commit diffs, run summaries). Paste your R2 S3 access key here.",
  },
] as const;

type ProviderId = (typeof SUPPORTED_PROVIDERS)[number]["id"];

interface Props {
  orgId: number;
  orgName?: string;
}

export function ProviderKeysPanel({ orgId, orgName }: Props) {
  const [keys, setKeys] = useState<ProviderKeySummary[]>([]);
  const [pasteValue, setPasteValue] = useState("");
  const [pasteProvider, setPasteProvider] = useState<ProviderId>("openrouter");
  const [pasteVisible, setPasteVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refreshKeys = useCallback(async () => {
    try {
      const resp = await listProviderKeys(orgId);
      setKeys(resp.keys);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not load provider keys. Refresh and try again.",
      );
    }
  }, [orgId]);

  useEffect(() => {
    void refreshKeys();
  }, [refreshKeys]);

  // Captain pivot: when a "missing OpenRouter key" error elsewhere
  // links back here with #provider-keys in the hash, scroll the
  // panel into view and highlight the paste input for ~3 seconds so
  // the Captain knows exactly where to paste.
  const pasteInputRef = useRef<HTMLInputElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [pulseHighlight, setPulseHighlight] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const check = () => {
      if (window.location.hash === "#provider-keys") {
        sectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        pasteInputRef.current?.focus();
        setPulseHighlight(true);
        window.setTimeout(() => setPulseHighlight(false), 3000);
      }
    };
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, []);

  async function savePastedKey(e: React.FormEvent) {
    e.preventDefault();
    if (pasteValue.trim() === "") return;
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await setProviderKey(orgId, pasteProvider, pasteValue.trim());
      setPasteValue("");
      setNotice(`${pasteProvider} key saved.`);
      await refreshKeys();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not save this key. Check it's a valid OpenRouter key and retry.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function revoke(provider: string) {
    const label = orgName ? `for ${orgName}` : "";
    if (!window.confirm(`Revoke the ${provider} key ${label}?`)) return;
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await revokeProviderKey(orgId, provider);
      setNotice(`${provider} key revoked.`);
      await refreshKeys();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not revoke this key. Refresh and try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section ref={sectionRef} id="provider-keys" className="flex flex-col gap-4">
      <div>
        <h2 className="text-sm font-semibold text-primary">Provider keys</h2>
        <p className="mt-1 text-xs text-muted">
          Bring your own key per ADR 0001. Waytide stores keys encrypted at
          rest and never bills tokens on its own account.
        </p>
      </div>

      <ul className="flex flex-col gap-2" data-testid="provider-keys-list">
        {keys.length === 0 && (
          <li className="text-sm text-muted" data-testid="provider-keys-empty">
            No provider key set yet. Paste an OpenRouter key below to start
            agent turns.
          </li>
        )}
        {keys.map((k) => (
          <li
            key={k.provider}
            className="flex items-center justify-between gap-3 border-l-2 border-edge bg-surface-raised px-3 py-2"
            data-testid={`provider-key-${k.provider}`}
          >
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-primary">
                {k.provider}
              </span>
              <span className="font-code text-xs text-muted">…{k.last4}</span>
            </div>
            <button
              onClick={() => void revoke(k.provider)}
              disabled={busy}
              className="cursor-pointer text-xs text-muted transition-colors hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
              data-testid={`provider-key-${k.provider}-revoke`}
            >
              Revoke
            </button>
          </li>
        ))}
      </ul>

      <form
        onSubmit={savePastedKey}
        className="flex flex-col gap-3 border-t border-edge pt-4"
        data-testid="provider-key-form"
      >
        <label className="flex flex-col gap-1 text-xs text-muted">
          Provider
          <select
            value={pasteProvider}
            onChange={(e) => setPasteProvider(e.target.value as ProviderId)}
            className="rounded-md border border-edge bg-surface-raised px-3 py-2 text-sm text-primary outline-none focus:border-border-highlight"
            data-testid="provider-key-provider"
          >
            {SUPPORTED_PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs text-faint">
          {SUPPORTED_PROVIDERS.find((p) => p.id === pasteProvider)?.hint}
        </p>
        <label className="flex flex-col gap-1 text-xs text-muted">
          Paste key
          <div className="relative">
            <input
              ref={pasteInputRef}
              type={pasteVisible ? "text" : "password"}
              value={pasteValue}
              onChange={(e) => setPasteValue(e.target.value)}
              placeholder="sk-or-v1-..."
              autoComplete="off"
              spellCheck={false}
              className={`glass-blur w-full rounded-md border bg-surface-glass px-3 py-2 pr-10 font-code text-sm text-primary outline-none focus:border-border-highlight ${
                pulseHighlight
                  ? "animate-pulse border-accent shadow-[0_0_0_2px_var(--wt-accent)]"
                  : "border-edge"
              }`}
              data-testid="provider-key-input"
            />
            <button
              type="button"
              onClick={() => setPasteVisible((v) => !v)}
              title={pasteVisible ? "Hide key" : "Show key"}
              aria-label={pasteVisible ? "Hide key" : "Show key"}
              className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-muted transition-colors hover:text-primary"
              data-testid="provider-key-toggle-visibility"
            >
              {pasteVisible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>
        <button
          type="submit"
          disabled={busy || pasteValue.trim() === ""}
          className="cursor-pointer self-start rounded-md bg-accent px-4 py-2 text-xs font-semibold text-[var(--wt-selection-text)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="provider-key-save"
        >
          {busy ? "Saving…" : "Save key"}
        </button>
      </form>

      {notice && (
        <p className="text-xs text-success" data-testid="settings-notice">
          {notice}
        </p>
      )}
      {error && (
        <p className="text-xs text-danger" data-testid="settings-error">
          {error}
        </p>
      )}
    </section>
  );
}
